import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection
} from '@angular/core';
import { provideClientHydration, withNoIncrementalHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router, withViewTransitions } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAuth } from '@core/auth/auth.provider';
import { authInterceptor } from '@core/auth/auth.interceptor';
import { SentryErrorHandlerInterceptor } from '@core/interceptors/sentry-error-handler.interceptor';
import { timezoneInterceptor } from '@core/interceptors/timezone.interceptor';
import { enableDialogExitAnimation } from '@core/motion/dialog-exit';
import { onAppViewTransition } from '@core/motion/view-transition';
import { initialAnimationsType, PerformanceModeService } from '@core/performance/performance-mode.service';
import { PublicThemeService } from '@core/theme/public-theme.service';
import * as Sentry from '@sentry/angular';
import { provideLottieOptions } from 'ngx-lottie';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { routes } from './app.routes';

registerLocaleData(localePt);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withViewTransitions({ skipInitialTransition: true, onViewTransitionCreated: onAppViewTransition })),
    provideClientHydration(withNoIncrementalHydration()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideEnvironmentNgxMask(),
    // Sem animações do Angular no modo desempenho e com "Reduzir movimento" (decidido antes do bootstrap)
    provideAnimationsAsync(initialAnimationsType()),
    provideHttpClient(withFetch(), withInterceptors([timezoneInterceptor, authInterceptor])),
    provideAuth(),
    provideLottieOptions({
      player: () => import(/* webpackChunkName: 'lottie-web' */ 'lottie-web')
    }),
    {
      provide: ErrorHandler,
      useFactory: () => new SentryErrorHandlerInterceptor()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      // Precisa existir antes da navegação inicial para ouvir o NavigationEnd (servidor e browser)
      inject(PublicThemeService);
      // Antes da primeira tela: em aparelho fraco a classe já está no <html> quando o Angular desenha
      inject(PerformanceModeService).init();
      // Folhas e diálogos descem ao fechar (no navegador; sem movimento fecham na hora)
      enableDialogExitAnimation();
    }),
    {
      provide: LOCALE_ID,
      useValue: 'pt'
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'BRL'
    }
  ]
};
